import type { EditorState, Transaction } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';

import { FIELD_NODE_NAME } from './fieldNode';
import type { FieldSuggestionItem } from './fieldSuggestion';

/**
 * A private MIME type, not `text/plain`.
 *
 * A palette entry dragged with `text/plain` would also be droppable into any other input on the
 * page, and would arrive in the editor as the literal text `{{ name }}` — the one form this
 * model exists to avoid. Keying on a type only the palette sets means a drop either produces a
 * real field node or is declined.
 */
export const FIELD_DRAG_MIME = 'application/x-dealstack-field';

/** The payload a palette entry carries. Serialised narrowly so a stray drop cannot be coerced. */
export const serialiseFieldPayload = (item: FieldSuggestionItem): string =>
	JSON.stringify({ name: item.name, label: item.label, filter: item.filter ?? null });

export const parseFieldPayload = (raw: string): FieldSuggestionItem | null => {
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return null;
	}
	if (typeof parsed !== 'object' || parsed === null) {
		return null;
	}
	const record: Record<string, unknown> = { ...parsed };
	const { name, label, filter } = record;
	if (typeof name !== 'string' || name === '') {
		return null;
	}
	return {
		name,
		label: typeof label === 'string' && label !== '' ? label : name,
		...(typeof filter === 'string' && filter !== '' ? { filter } : {}),
	};
};

/**
 * A transaction inserting a field at `pos`, or null if a field cannot go there.
 *
 * Checked rather than assumed: a drop can land on a position whose parent takes no inline
 * content — between two table rows, say — and inserting there would throw inside the dispatch
 * and take the editor down with it.
 */
export const insertFieldAt = (
	state: EditorState,
	pos: number,
	item: FieldSuggestionItem
): Transaction | null => {
	const fieldType = state.schema.nodes[FIELD_NODE_NAME];
	if (fieldType === undefined) {
		return null;
	}
	const node = fieldType.create({ name: item.name, filter: item.filter ?? null });
	const $pos = state.doc.resolve(pos);
	if (!$pos.parent.canReplaceWith($pos.index(), $pos.index(), fieldType)) {
		return null;
	}
	return state.tr.insert(pos, node);
};

/** Wires the two together. `posAtCoords` is the only part that needs a live view. */
export const handleFieldDrop = (view: EditorView, event: DragEvent): boolean => {
	const raw = event.dataTransfer?.getData(FIELD_DRAG_MIME);
	if (raw === undefined || raw === '') {
		return false;
	}
	const item = parseFieldPayload(raw);
	if (item === null) {
		return false;
	}
	const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
	if (coords === null) {
		return false;
	}
	const transaction = insertFieldAt(view.state, coords.pos, item);
	if (transaction === null) {
		return false;
	}
	event.preventDefault();
	view.dispatch(transaction);
	return true;
};
